import { Controller } from '@hotwired/stimulus'

const MIN_SKELETON_MS = 500

export default class extends Controller {
  static targets = ['checkboxAll', 'checkbox']

  initialize () {
    this.toggle = this.toggle.bind(this)
    this.refresh = this.refresh.bind(this)
  }

  setStepState (stepTarget, state) {
    if (stepTarget) stepTarget.dataset.state = state
  }

  get projectId () {
    const hidden = this.projectSelectTarget.querySelector('input[type="hidden"]')
    return hidden?.value || this.projectSelectTarget.value
  }

  showSkeleton (frame, html) {
    if (!frame) return
    frame.innerHTML = html
    this.skeletonShownAt = performance.now()
    if (!this.holdSkeletonBound) {
      this.holdSkeletonBound = this.holdSkeleton.bind(this)
      this.element.addEventListener('turbo:before-frame-render', this.holdSkeletonBound)
    }
  }

  holdSkeleton (event) {
    if (!this.skeletonShownAt) return
    const remaining = MIN_SKELETON_MS - (performance.now() - this.skeletonShownAt)
    this.skeletonShownAt = null
    if (remaining <= 0 || typeof event.detail?.resume !== 'function') return
    event.preventDefault()
    setTimeout(() => event.detail.resume(), remaining)
  }

  cardsGridSkeleton () {
    const widths = ['w-3/4', 'w-2/3', 'w-4/5']
    const cards = widths.map((w) => `
      <div class="min-h-[140px] rounded-2xl border border-gray-100 p-4 flex flex-col justify-between animate-pulse">
        <div class="h-4 rounded-full bg-gray-200 ${w}"></div>
        <div class="h-4 w-4 rounded bg-gray-100 self-end"></div>
      </div>
    `).join('')
    return `<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">${cards}</div>`
  }

  chipsSkeleton () {
    const widths = ['w-20', 'w-24', 'w-16', 'w-28', 'w-20']
    const chips = widths.map((w) => `<div class="${w} h-9 rounded-full bg-gray-100 animate-pulse"></div>`).join('')
    return `<div class="flex flex-wrap gap-2 justify-center">${chips}</div>`
  }

  listSkeleton () {
    const widths = ['w-11/12', 'w-3/4', 'w-5/6', 'w-2/3', 'w-4/5']
    const rows = widths.map((w) => `
      <div class="flex items-center gap-3 px-3 py-2.5 animate-pulse">
        <div class="w-5 h-5 rounded bg-gray-200 shrink-0"></div>
        <div class="h-4 rounded-full bg-gray-200 ${w}"></div>
        <div class="h-3 w-20 rounded-full bg-gray-100 ml-auto shrink-0"></div>
      </div>
    `).join('')
    return `
      <div class="flex items-center justify-between mb-2 pb-3 border-b border-gray-100 animate-pulse">
        <div class="h-4 w-24 rounded-full bg-gray-200"></div>
        <div class="h-7 w-32 rounded-full bg-gray-100"></div>
      </div>
      <div class="space-y-1">${rows}</div>
    `
  }

  resetSubmitButton () {
    this.submitButtonDisabled = true
    this.submitButtonVisible = false
  }

  // eslint-disable-next-line accessor-pairs
  set submitButtonDisabled (value) {
    this.submitButtonTarget.disabled = value
  }

  // eslint-disable-next-line accessor-pairs
  set submitButtonVisible (value) {
    if (value) {
      this.submitButtonTarget.classList.remove('hidden')
    } else {
      this.submitButtonTarget.classList.add('hidden')
    }
  }

  checkboxAllTargetConnected (checkbox) {
    checkbox.addEventListener('change', this.toggle)
    this.refresh()
  }

  checkboxTargetConnected (checkbox) {
    checkbox.addEventListener('change', this.refresh)
    this.refresh()
  }

  checkboxAllTargetDisconnected (checkbox) {
    checkbox.removeEventListener('change', this.toggle)
    this.refresh()
  }

  checkboxTargetDisconnected (checkbox) {
    checkbox.removeEventListener('change', this.refresh)
    this.refresh()
  }

  toggle (e) {
    e.preventDefault()

    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = e.target.checked
      this.triggerInputEvent(checkbox)
    })
  }

  refresh () {
    if (!this.hasCheckboxAllTarget) return

    const checkboxesCount = this.checkboxTargets.length
    const checkboxesCheckedCount = this.checked.length

    this.checkboxAllTarget.checked = checkboxesCheckedCount > 0
    this.checkboxAllTarget.indeterminate = checkboxesCheckedCount > 0 && checkboxesCheckedCount < checkboxesCount
  }

  triggerInputEvent (checkbox) {
    const event = new Event('input', { bubbles: false, cancelable: true })

    checkbox.dispatchEvent(event)
  }

  get checked () {
    return this.checkboxTargets.filter(checkbox => checkbox.checked)
  }

  get unchecked () {
    return this.checkboxTargets.filter(checkbox => !checkbox.checked)
  }
}
